import { Button, MuiAlert, ResponsiveDrawer, Select, strIsNullOrEmpty } from '@fox/atoms';
import { ResponsiveTable } from '@fox/data-grid';
import { FlexRowSpaced } from '@fox/layout';
import { useMediaQuery } from '@mui/material';
import MUIAlert from '@mui/material/Alert';
import React, { useState } from 'react';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';
import { sanitiseFileName } from '../../../common/helper/helper';
import { useContentContext } from '../../content-context';
import { TableWrapper } from '../financial-details/receipts';
import { renderDate } from '../financial-details/shared';
import { useActionCentre, useActionCentreSources } from '../query-hooks/query-implementations';
import { ActionCentreColumn, SwiftFoxActionCentreResult } from './contracts';

interface IActionCentreTableProps {
	actionId: string;
	rowsExpandedOnSmallScreens: boolean;
}

export const ActionCentre: React.FC<IActionCentreTableProps> = ({ actionId, rowsExpandedOnSmallScreens }) => {
	const { contentId, requestToken } = useContentContext();
	const [sourceFilter, setSourceFilter] = useState<number | undefined>(undefined);

	const { data: sources, error, loading } = useActionCentreSources({ contentId, requestToken, actionId });

	if (!strIsNullOrEmpty(error)) return <MUIAlert severity="error">{error}</MUIAlert>;
	if (loading) return null;
	const sourceOptions = sources ? Object.entries(sources) : [];
	const filter = sourceFilter ?? Number(sourceOptions?.[0]?.[0]);

	return (
		<TableWrapper key={filter}>
			<FlexRowSpaced style={{ marginTop: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
				{sourceOptions.length ? (
					<Select<[string, string], false, false, false>
						getOptionLabel={([key, value]) => value}
						options={sourceOptions}
						value={sourceOptions.find(([key, value]) => key == filter?.toString())}
						helperText=""
						style={{ minWidth: '300px' }}
						onChange={(e, [key, value]) => setSourceFilter(Number(key))}
					/>
				) : null}
				<FlexRowSpaced>
					<ExportButton actionId={actionId} source={sourceOptions.find(([key, value]) => key == filter?.toString())} />
					<PdfButton actionId={actionId} sourceFilter={filter} />
				</FlexRowSpaced>
			</FlexRowSpaced>
			<ActionCentreTable actionId={actionId} sourceFilter={filter} rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens} />
		</TableWrapper>
	);
};

function ActionCentreTable({
	actionId,
	sourceFilter,
	rowsExpandedOnSmallScreens
}: {
	actionId: string;
	sourceFilter: number;
	rowsExpandedOnSmallScreens: boolean;
}) {
	const { contentId, requestToken } = useContentContext();
	const [page, setPage] = useState<number>(1);

	const PAGE_SIZE = 10;
	const { data, error, loading } = useActionCentre({
		contentId,
		requestToken,
		actionId,
		page: page - 1,
		pageSize: PAGE_SIZE,
		sourceFilter,
		triggers: ['page', 'sourceFilter']
	});

	if (!strIsNullOrEmpty(error)) return <MUIAlert severity="error">{error}</MUIAlert>;

	const columns = data?.columns?.map((column, i) => {
		return {
			title: column.title,
			dataIndex: column.id,
			showOnMobileRow: i <= 3 ? true : false
		};
	});

	return data?.data?.length === 0 ? (
		<MUIAlert severity="info">No data to display</MUIAlert>
	) : (
		<ResponsiveTable<SwiftFoxActionCentreResult['data'][0]>
			data={data?.data}
			rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
			columns={columns}
			paginationProps={
				data?.totalResults
					? {
							page,
							onPageChange: page => setPage(page),
							total: data?.totalResults,
							itemsPerPage: PAGE_SIZE
					  }
					: undefined
			}
		/>
	);
}

function PdfButton({ actionId, sourceFilter }: { actionId: string; sourceFilter?: number }) {
	const { contentId, requestToken } = useContentContext();
	const [loading, setLoading] = useState<boolean>(false);
	const [pdf, setPdf] = useState<string | null>(null);
	const [error, setError] = useState<string>('');

	const isMobile = useMediaQuery('(max-width: 800px)');

	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;

	return (
		<>
			<Button
				disabled={loading}
				startIconName="FilePdf1"
				onClick={async () => {
					setLoading(true);
					setError('');
					let url = `/Umbraco/Api/Membership/ActionCentrePdf?contentId=${contentId}&actionId=${encodeURIComponent(actionId)}`;
					if (sourceFilter) {
						url += `&sourceFilter=${sourceFilter}`;
					}
					const res = await fetch(url, {
						method: 'GET',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
							RequestVerificationToken: requestToken
						}
					});
					if (res.ok) {
						var result = await res.json();
						if (result.success === true) {
							setPdf(result.result);
						} else {
							setError(result.message);
						}
					} else {
						const error = await res.text();
						setError(error);
					}
					setLoading(false);
				}}
				variant="primary"
			>
				PDF
			</Button>
			{isMobile ? (
				<ResponsiveDrawer
					placement="bottom"
					title="Export to PDF"
					width="100%"
					height="100%"
					style={{ height: '100%' }}
					open={!strIsNullOrEmpty(pdf)}
					onClose={() => setPdf(null)}
				>
					{pdf ? <iframe title="Export to PDF" width="100%" height="100%" src={pdf}></iframe> : <></>}
				</ResponsiveDrawer>
			) : (
				<ResponsiveDrawer open={!strIsNullOrEmpty(pdf)} width={800} title="Export to PDF" onClose={() => setPdf(null)}>
					<>{pdf ? <iframe title="Export to PDF" width="100%" height="100%" src={pdf}></iframe> : <></>}</>
				</ResponsiveDrawer>
			)}
		</>
	);
}

function formatDataAndHeaders(
	columns: ActionCentreColumn[],
	data: Record<string, string>[],
	name: string
): { formattedData: Record<string, string>[]; headers: { key: string; label: string }[] } {
	let formattedData = [
		{ c0: name ?? '' },
		{},
		Object.entries(data?.[0] ?? {}).reduce<Record<string, string>>((prev, [key, value], i) => {
			const columnName = columns.find(c => c.id === key).title;
			prev[`c${i}`] = columnName;
			return prev;
		}, {})
	];
	formattedData = [
		...formattedData,
		...data.map((record: Record<string, string>, i) => {
			return Object.entries(record).reduce<Record<string, string>>((prev, [key, value], i) => {
				prev[`c${i}`] = value;
				return prev;
			}, {});
		})
	];

	const headers = columns.map((c, i) => ({ key: `c${i}`, label: '' }));

	return { formattedData, headers };
}

function ExportButton({ actionId, source }: { actionId: string; source?: [string, string] }) {
	const { contentId, requestToken } = useContentContext();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [data, setData] = useState<Record<string, string>[]>(undefined);
	const [headers, setHeaders] = useState<{ key: string; label: string }[]>(undefined);
	const [actionCentreName, setActionCentreName] = useState<string>(undefined);

	const [sourceFilter, sourceName] = source ?? [];

	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;

	if (!data) {
		return (
			<Button
				startIconName="Download"
				disabled={loading}
				onClick={async () => {
					setLoading(true);
					setError('');
					let url = `/Umbraco/Api/Membership/ActionCentreData?contentId=${contentId}&actionId=${encodeURIComponent(actionId)}`;
					if (sourceFilter) {
						url += `&sourceFilter=${sourceFilter}`;
					}
					const res = await fetch(url, {
						method: 'GET',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
							RequestVerificationToken: requestToken
						}
					});
					if (res.ok) {
						var result = await res.json();
						if (result.success === true) {
							const name = `${result.result.name}${sourceName ? ` - ${sourceName}` : ''} - ${renderDate(new Date())}`;
							const { formattedData, headers } = formatDataAndHeaders(result.result.columns, result.result.data, name);
							setData(formattedData);
							setHeaders(headers);
							setActionCentreName(sanitiseFileName(name));
						} else {
							setError(result.message);
						}
					} else {
						const error = await res.text();
						setError(error);
					}
					setLoading(false);
				}}
				variant="primary"
			>
				Export
			</Button>
		);
	}

	return (
		<FlexRowSpaced>
			<Button variant="tertiary" onClick={() => setData(undefined)}>
				Cancel
			</Button>
			<ButtonLinkWrapper>
				<CSVLink
					data={data ?? []}
					headers={headers}
					filename={actionCentreName ? `${actionCentreName}.csv` : `export_${renderDate(new Date())}.csv`}
					enclosingCharacter={`"`}
				>
					<Button
						sx={{ height: '100%' }}
						startIconName="Download"
						variant="primary"
						title="Download File"
						onClick={() => setData(undefined)}
					>
						Download Export
					</Button>
				</CSVLink>
			</ButtonLinkWrapper>
		</FlexRowSpaced>
	);
}

export const ButtonLinkWrapper = styled.div`
	a {
		color: inherit;
		text-decoration: inherit;
	}
`;
